import { useI18n } from "@/i18n";
import { loungeCopy } from "@/lib/loungeCopy";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { LoungeSectionTitle } from "@/components/lounge/LoungeShell";

// Hero editoriale full-bleed con immagine di sfondo. Gradiente verso il colore di
// sfondo del tema (Dark Blu in dark, bianco sporco in light) per leggibilità del testo.
export function LoungeHero({ image, eyebrow, title, subtitle, children }) {
  const src = resolveLoungeUrl(image);
  return (
    <section className="relative overflow-hidden" data-testid="lounge-hero">
      {src && <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/30" />
      <div className="relative mx-auto flex min-h-[68vh] max-w-6xl flex-col justify-end px-5 pb-14 pt-32 sm:px-8 sm:pb-20">
        <div className="max-w-2xl">
          <div className="mb-3 inline-flex rounded-full border border-brand/40 bg-brand/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-brand">
            {eyebrow}
          </div>
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-4 max-w-xl text-lg text-muted-foreground">{subtitle}</p>
          {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        </div>
      </div>
    </section>
  );
}

// Introduzione brand: Storia · Performance · Lifestyle. `intro` = override per lingua.
export function BrandIntro({ intro }) {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  const ov = (intro || {})[lang] || {};
  const g = (key) => (ov[key] !== undefined && ov[key] !== "" ? ov[key] : c[key]);
  const blocks = [
    { n: "01", t: g("story_title"), p: g("story_p") },
    { n: "02", t: g("perf_title"), p: g("perf_p") },
    { n: "03", t: g("life_title"), p: g("life_p") },
  ];
  return (
    <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-brand-intro">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
        {blocks.map((b) => (
          <div key={b.n} className="border-t border-border pt-6">
            <div className="text-sm font-bold text-brand">{b.n}</div>
            <h3 className="mt-2 text-2xl font-bold text-foreground">{b.t}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{b.p}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Griglia di 4 immagini quadrate.
export function Gallery({ images = [], title }) {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  return (
    <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8 sm:pb-24" data-testid="lounge-gallery">
      <LoungeSectionTitle title={title || c.gallery_title} testId="lounge-gallery-title" />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {images.slice(0, 4).map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden rounded-xl border border-border bg-muted" data-testid={`lounge-gallery-img-${i}`}>
            {resolveLoungeUrl(src) ? (
              <img
                src={resolveLoungeUrl(src)}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}
